<template>
  <div class="slider">
    <vue-slider
      v-model="amount"
      @change="updateValue($event)"
      :min="min"
      :max="max"
      :marks="marks"
      :tooltip-formatter="formatter"
      :process-style="{ backgroundColor: '#178DC2', height: '8px' }"
      :rail-style="{ height: '8px' }"
      :tooltip-style="{ backgroundColor: '#178DC2', borderColor: '#178DC2' }"
    >
      <template v-slot:dot>
        <div :class="['custom-dot']">
          <div></div>
          <div></div>
          <div></div>
        </div>
      </template>
      <template v-slot:label="{ active, value }">
        <div :class="['vue-slider-mark-label', { active }]">{{ formatInMoney(value) }}</div>
      </template>
    </vue-slider>
    <div class="slider-right">
      <div class="input-wrapper">
        <span class="euro-sign">&euro;</span>
        <input v-model="amount" @blur="checkValueAndUpdateValue($event)" />
      </div>
      <div class="l-link">
        <a href="#" @click="$emit('infoButtonClicked', $event)">
          <strong>{{ infoButtonText }}</strong>
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import * as BofiUtils from "../../utils";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";

export default {
  name: "Slider",
  components: {
    VueSlider,
  },
  props: {
    infoButtonText: String,
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    value: Number,
  },
  data() {
    return {
      amount: 0,
      formatter: v => "€ " + BofiUtils.formatMoney(v),
    };
  },
  beforeMount() {
    this.amount = this.getSaveValue(this.value);
    this.updateValue();
  },
  watch: {
    value: function(newValue) {
      this.amount = this.getSaveValue(newValue);
    },
    min: function(newVal) {
      if (this.amount < newVal) {
        this.amount = newVal;
      }
    },
    max: function(newVal) {
      if (this.amount > newVal) {
        this.amount = newVal;
      }
    },
  },
  computed: {
    marks() {
      return [this.min, this.max];
    },
    formattedMax() {
      return BofiUtils.formatMoney(this.max);
    },
    formattedMin() {
      return BofiUtils.formatMoney(this.min);
    },
  },
  methods: {
    getSaveValue(value) {
      let safeValue = value > this.max ? this.max : value;
      return safeValue < this.min ? this.min : safeValue;
    },
    checkValueAndUpdateValue() {
      if (this.amount < this.min) {
        this.amount = this.min;
      }

      if (this.amount > this.max) {
        this.amount = this.max;
      }

      this.updateValue();
    },
    updateValue: function() {
      this.$emit("input", parseInt(this.amount, 10));
    },
    formatInMoney: function(value) {
      return BofiUtils.formatMoney(value);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@bvm/bofi/src/assets/scss/settings/_settings.scss";

.slider {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  @include for-size(tablet-portrait-up) {
    flex-direction: row;
  }

  .vue-slider {
    width: 100% !important;
    @include for-size(tablet-portrait-up) {
      margin-right: 30px;
    }
    margin-top: 20px;
  }

  .vue-slider-marks .vue-slider-mark {
    .vue-slider-mark-label {
      font-weight: bold;
      font-size: rem-calc(12);
      color: $color-grey;

      @include for-size(desktop-up) {
        font-size: 14px;
      }
    }

    &:first-child {
      .vue-slider-mark-label {
        position: relative;
      }
    }

    &:last-child {
      .vue-slider-mark-label {
        position: relative;
        text-align: right;
        width: 100px;
        margin-left: -50px;
        @include for-size(desktop-up) {
          margin-left: 0;
          width: 100px;
          padding-right: 50px;
        }
      }
    }
  }

  .custom-dot {
    width: 30px;
    height: 30px;
    background-color: #178DC2;
    border-radius: 50%;
    margin-top: -8px;
    margin-left: -8px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);

    div {
      width: 2px;
      height: 60%;
      background: rgba(0, 0, 0, 0.15);
      margin: 0 2px;

      &:first-child,
      &:last-child {
        height: 40%;
      }
    }
  }

  .slider-right {
    width: 100%;

    @include for-size(tablet-portrait-up) {
      width: 200px;
    }

    .input-wrapper {
      height: 52px;
      border: 2px solid $color-cotton;
      margin-bottom: 5px;
      border-radius: 3px;
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 10px 10px 10px;
      font-size: 1.5rem;
      font-weight: bold;
      flex-wrap: wrap;
      margin-top: 40px;

      @include for-size(tablet-portrait-up) {
        max-width: calc(100% - 30px);
        margin-top: 3px;
      }

      &:before {
        content: "";
        position: absolute;
        top: 0;
        left: calc(50% + 3px);
        width: 0;
        height: 0;
        border: 8px solid transparent;
        border-bottom-color: $color-cotton;
        margin-left: -8px;
        margin-top: -8px;
        @media screen and(max-width: 768px) {
          border-top: 0;
        }

        @include for-size(tablet-portrait-up) {
          border: 8px solid transparent;
          border-right-color: $color-cotton;
          border-left: 0;
          top: 50%;
          left: 0;
          margin-left: -8px;
          margin-top: -7px;
        }
      }

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: calc(50% + 3px);
        width: 0;
        height: 0;
        border: 6px solid transparent;
        border-bottom-color: $color-white;
        margin-left: -6px;
        margin-top: -6px;

        @media screen and(max-width: 768px) {
          border-top: 0;
        }

        @include for-size(tablet-portrait-up) {
          border: 8px solid transparent;
          border-right-color: $color-white;
          border-left: 0;
          top: 50%;
          left: 0;
          margin-left: -6px;
          margin-top: -6px;
        }
      }

      .euro-sign {
        font-size: rem-calc(24);
        color: $color-cotton;
      }

      input {
        width: 85%;
        padding-right: 32px;
        text-align: center;
        font-size: rem-calc(24);
        color: $color-black-87;
        border: 0;

        @include for-size(tablet-portrait-up) {
          padding-right: 0;
        }

        &:focus {
          outline: none;
        }
      }
    }

    @media (min-width: 600px) {
      .input-wrapper {
        max-width: 100%;
        margin-top: 3px;

        &:before {
          border: 8px solid transparent;
          border-right-color: $color-cotton;
          border-left: 0;
          top: 50%;
          left: 0;
          margin-left: -8px;
          margin-top: -7px;
        }
      }
    }

    .l-link {
      margin-left: 2px;
      font-size: 14px;
    }
  }
}
</style>
